import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

interface PricingProps {
  isDarkMode: boolean;
}

const TIERS = [
  {
    name: 'Development',
    tag: '01 / Build',
    description: 'End-to-end utility app development — from concept and UI/UX design to launch-ready product.',
    image:
      '/price-1.webp',
    features: [
      'Mobile & desktop utility apps',
      'Full UI/UX design included',
      'QA testing & bug fixing',
      'App Store & Play Store submission',
      '30-day post-launch support',
    ],
    highlight: false,
  },
  {
    name: 'Marketing',
    tag: '02 / Growth',
    description: 'Full-cycle app promotion: ASO, paid user acquisition, creative production, and analytics.',
    image:
      'price-2.webp',
    features: [
      'ASO & keyword optimisation',
      'Media buying (Meta, Google, TikTok)',
      'Ad creative production',
      'A/B testing & analytics',
      'Monthly performance reports',
      'Influencer campaign management',
    ],
    highlight: true,
  },
  {
    name: 'Full Package',
    tag: '03 / Complete',
    description: 'Development + marketing under one roof. A dedicated team handles everything from code to installs.',
    image:
      '/price-3.webp',
    features: [
      'Everything in Development',
      'Everything in Marketing',
      'Dedicated project manager',
      'Weekly progress calls',
      'Priority support & escalation',
      'Quarterly strategy reviews',
    ],
    highlight: false,
  },
];

const ADDONS = [
  { code: '+A', name: 'AI Integration', desc: 'OpenAI, Claude, or custom model integration.' },
  { code: '+B', name: 'Backend & API', desc: 'Cloud infrastructure, APIs, and database design.' },
  { code: '+C', name: 'Extra platform', desc: 'Additional platform beyond primary target.' },
  { code: '+D', name: 'Ongoing maintenance', desc: 'Monthly updates, bug fixes, and monitoring.' },
];

const Pricing = ({ isDarkMode }: PricingProps) => {
  const sectionRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const animEls = sectionRef.current?.querySelectorAll('[data-anim]') ?? [];
      const tierEls = sectionRef.current?.querySelectorAll('[data-tier]') ?? [];
      const addonEls = sectionRef.current?.querySelectorAll('[data-addon]') ?? [];

      if (animEls.length) {
        gsap.fromTo(
          animEls,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: 'power3.out',
            stagger: 0.08,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 85%',
              once: true,
            },
          }
        );
      }
      if (tierEls.length) {
        gsap.fromTo(
          tierEls,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: 'power3.out',
            stagger: 0.12,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 70%',
              once: true,
            },
          }
        );
      }
      if (addonEls.length) {
        gsap.fromTo(
          addonEls,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: 'power3.out',
            stagger: 0.08,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 40%',
              once: true,
            },
          }
        );
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="pricing"
      className={`relative w-full py-32 md:py-44 px-6 md:px-12 transition-colors duration-700 ${
        isDarkMode ? 'bg-[#0a0a0a] text-white' : 'bg-white text-black'
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* ───── Header — editorial split layout ───── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 md:mb-20 items-end">
          <div className="lg:col-span-7">
            <div
              data-anim
              className={`mb-8 inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] font-mono uppercase tracking-[0.2em] ${
                isDarkMode
                  ? 'border-white/20 text-white/70 bg-white/5'
                  : 'border-black/15 text-black/60 bg-black/5'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current" />
              03 — Plans
            </div>
            <h2
              data-anim
              className="text-[2.5rem] md:text-[4.5rem] lg:text-[5.5rem] leading-[0.92] font-bold tracking-tighter"
            >
              Three packages.
              <br />
              One <span className="italic font-light">clear investment</span>.
            </h2>
          </div>

          <div className="lg:col-span-5 lg:pb-3">
            <p
              data-anim
              className={`text-base md:text-lg leading-relaxed ${
                isDarkMode ? 'text-white/65' : 'text-black/65'
              }`}
            >
              All packages include project management, regular progress updates, and a dedicated Slack channel. Custom quotes available for multi-platform or enterprise requirements — reach out and we'll prepare a tailored proposal.
            </p>
          </div>
        </div>

        {/* ───── Tier cards ───── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {TIERS.map((tier) => {
            const isHighlight = tier.highlight;
            return (
              <div
                key={tier.name}
                data-tier
                className={`relative rounded-2xl border overflow-hidden flex flex-col transition-all duration-500 ${
                  isHighlight
                    ? isDarkMode
                      ? 'bg-white text-black border-white'
                      : 'bg-black text-white border-black'
                    : isDarkMode
                    ? 'bg-[#0f0f0f] border-white/12'
                    : 'bg-white border-black/10'
                }`}
              >
                {/* Micro image header */}
                <div className="relative w-full h-40 md:h-44 overflow-hidden">
                  <img
                    src={tier.image}
                    alt={`${tier.name} sample portrait`}
                    className="w-full h-full object-cover object-top grayscale"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                  {isHighlight && (
                    <span
                      className={`absolute top-4 right-4 px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.2em] backdrop-blur ${
                        isDarkMode ? 'bg-black/70 text-white' : 'bg-white/80 text-black'
                      }`}
                    >
                      Most popular
                    </span>
                  )}
                  <span
                    className={`absolute top-4 left-4 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.25em] backdrop-blur ${
                      isDarkMode ? 'bg-black/60 text-white/85' : 'bg-white/75 text-black/75'
                    }`}
                  >
                    {tier.tag}
                  </span>
                </div>

                {/* Body */}
                <div className="p-8 md:p-10 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-semibold tracking-tight">{tier.name}</h3>
                  </div>

                  <p
                    className={`text-sm leading-relaxed mb-8 ${
                      isHighlight
                        ? isDarkMode
                          ? 'text-black/60'
                          : 'text-white/65'
                        : isDarkMode
                        ? 'text-white/55'
                        : 'text-black/55'
                    }`}
                  >
                    {tier.description}
                  </p>

                  <ul className="flex-1 space-y-3 mb-8">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm">
                        <span
                          className={`mt-0.5 w-4 h-4 flex items-center justify-center rounded-full ${
                            isHighlight
                              ? isDarkMode
                                ? 'bg-black/10 text-black'
                                : 'bg-white/15 text-white'
                              : isDarkMode
                              ? 'bg-white/10 text-white'
                              : 'bg-black/10 text-black'
                          }`}
                        >
                          <i className="ri-check-line text-[10px]"></i>
                        </span>
                        <span
                          className={
                            isHighlight
                              ? isDarkMode
                                ? 'text-black/80'
                                : 'text-white/85'
                              : isDarkMode
                              ? 'text-white/75'
                              : 'text-black/75'
                          }
                        >
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contact"
                    onClick={() => window.scrollTo(0, 0)}
                    className={`w-full rounded-full py-3.5 text-sm font-medium hover:scale-[1.02] transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${
                      isHighlight
                        ? isDarkMode
                          ? 'bg-black text-white hover:bg-[#1a1a1a]'
                          : 'bg-white text-black hover:bg-gray-100'
                        : isDarkMode
                        ? 'bg-white text-black hover:bg-gray-200'
                        : 'bg-black text-white hover:bg-gray-900'
                    }`}
                  >
                    <span>Contact Us</span>
                    <i className="ri-arrow-right-up-line text-base"></i>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* ───── Add-ons strip ───── */}
        <div className="mt-24 md:mt-32">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <span
                data-addon
                className={`block mb-3 text-[11px] font-mono uppercase tracking-[0.25em] ${
                  isDarkMode ? 'text-white/45' : 'text-black/45'
                }`}
              >
                04 — Add-ons
              </span>
              <h3
                data-addon
                className="text-2xl md:text-4xl font-bold tracking-tight leading-tight"
              >
                A la carte, when the brief calls for it.
              </h3>
            </div>
            <span
              data-addon
              className={`text-sm font-mono ${
                isDarkMode ? 'text-white/40' : 'text-black/40'
              }`}
            >
              [ stackable on any tier ]
            </span>
          </div>

          <div
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t ${
              isDarkMode ? 'border-white/10' : 'border-black/10'
            }`}
          >
            {ADDONS.map((a, i) => (
              <div
                key={a.name}
                data-addon
                className={`relative p-6 md:p-7 border-b transition-colors duration-300 ${
                  isDarkMode
                    ? 'border-white/10 hover:bg-white/[0.03]'
                    : 'border-black/10 hover:bg-black/[0.02]'
                } ${
                  i !== ADDONS.length - 1
                    ? isDarkMode
                      ? 'lg:border-r lg:border-white/10'
                      : 'lg:border-r lg:border-black/10'
                    : ''
                }`}
              >
                <div className="flex items-center justify-between mb-6">
                  <span
                    className={`text-[10px] font-mono uppercase tracking-[0.25em] ${
                      isDarkMode ? 'text-white/40' : 'text-black/40'
                    }`}
                  >
                    {a.code}
                  </span>
                  <i
                    className={`ri-add-line text-base ${
                      isDarkMode ? 'text-white/40' : 'text-black/40'
                    }`}
                  ></i>
                </div>
                <h4 className="text-lg font-semibold tracking-tight mb-2">{a.name}</h4>
                <p
                  className={`text-sm ${
                    isDarkMode ? 'text-white/55' : 'text-black/55'
                  }`}
                >
                  {a.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ───── Fine print row ───── */}
        <div
          data-addon
          className={`mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 pt-10 border-t ${
            isDarkMode ? 'border-white/10' : 'border-black/10'
          }`}
        >
          {[
            {
              k: '50%',
              label: 'Deposit',
              t: 'Non-refundable, due at project kick-off. Balance on completion.',
            },
            {
              k: 'NET 14',
              label: 'Invoicing',
              t: 'Agencies and enterprise clients billed on standard NET 14 terms.',
            },
            {
              k: '+20%',
              label: 'UK VAT',
              t: 'Applied to all service fees for UK-registered businesses.',
            },
          ].map((row) => (
            <div key={row.label} className="flex items-start gap-5">
              <span
                className={`text-3xl md:text-4xl font-bold tracking-tighter leading-none ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}
              >
                {row.k}
              </span>
              <div>
                <span
                  className={`block text-[10px] font-mono uppercase tracking-[0.25em] mb-2 ${
                    isDarkMode ? 'text-white/45' : 'text-black/45'
                  }`}
                >
                  {row.label}
                </span>
                <p
                  className={`text-sm leading-relaxed ${
                    isDarkMode ? 'text-white/60' : 'text-black/60'
                  }`}
                >
                  {row.t}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;